import { useEffect, useRef, useState } from 'react'
import { Mail, X, toast } from '@ensafely/checker-ui'
import { decodeToken, getToken, setToken, useAuthToken } from '../lib/authToken'

/**
 * Reads `email_verified` from the stored JWT and shows a dismissible banner
 * when the field is false (or absent for old tokens).
 *
 * The token is read through the authToken store, so when the verification page
 * (or another tab) swaps in a fresh JWT the banner re-renders and disappears —
 * no reload required.
 *
 * On mount, if the JWT says unverified, we call POST /api/auth/refresh-token
 * to get a fresh JWT from the DB. This covers the case where the user verified
 * on another device (e.g. mobile) and the current session's token is stale.
 */
function isVerified(token: string | null): boolean {
  if (!token) return true // Not logged in — no banner
  const payload = decodeToken(token)
  if (!payload) return true
  return payload.email_verified !== false
}

async function tryRefreshToken(): Promise<void> {
  try {
    const token = getToken()
    if (!token) return
    const res = await fetch('/api/auth/refresh-token', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return
    const body = await res.json()
    // Only adopt the refreshed JWT when it actually says "verified": a refresh
    // started before the /verify-email POST landed would otherwise race and
    // overwrite the fresh verified token with a stale unverified one.
    if (body.token && isVerified(body.token)) setToken(body.token)
  } catch {
    /* network error — keep showing banner */
  }
}

async function resendVerificationEmail(): Promise<void> {
  const res = await fetch('/api/auth/resend-verification', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error ?? 'Failed to resend verification email')
  }
}

export function EmailVerificationBanner() {
  const [dismissed, setDismissed] = useState(false)
  const [sending, setSending] = useState(false)
  const token = useAuthToken()
  const verified = isVerified(token)

  // On mount: if JWT says unverified, try refreshing from DB once.
  // Covers cross-device verification (e.g. confirmed on mobile).
  const refreshed = useRef(false)
  useEffect(() => {
    if (verified || refreshed.current) return
    refreshed.current = true
    void tryRefreshToken()
  }, [verified])

  if (dismissed || verified) return null

  const handleResend = async () => {
    setSending(true)
    try {
      await resendVerificationEmail()
      toast.success('Verification email sent! Check your inbox.')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send email'
      toast.error(msg)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="w-full bg-amber-50 border-b border-amber-200 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-2 flex items-center gap-2 text-amber-800 dark:text-amber-200 text-sm">
      <Mail className="h-4 w-4 flex-shrink-0" />
      <span>Please verify your email within 3 days. Unverified accounts are automatically deleted.</span>
      <button
        onClick={handleResend}
        disabled={sending}
        className="ml-1 font-medium underline hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {sending ? 'Sending…' : 'Resend verification email'}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="ml-auto flex-shrink-0 p-0.5 rounded hover:bg-amber-100 dark:hover:bg-amber-900"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
